import { IApprovalTaskItem } from '@ebb-v2/ebb-integration';
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl, FormGroupDirective, NgForm, AbstractControl } from '@angular/forms';

import {
  AuthenticationService,
  ApplicationApiService
} from '../../data-services/index';

import {
  IApplication, IApplicationInput, IApprovalTask, IHelp, IUser
} from '@ebb-v2/ebb-integration';

import { AppSheetHelpComponent } from '../../chrome/app-sheet-help.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ErrorStateMatcher } from '@angular/material/core';

class CrossFieldErrorMatcherConstructionDates implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (control.touched && control.errors) || (form.errors && form.errors.startEndDateOrder);
  }
}

@Component({
  selector: 'ebb-portal-timespan-form',
  templateUrl: './timespan-form.component.html'
})
export class TimespanFormComponent implements OnInit {
  @Input('application') application: IApplication;
  @Input('task') task: IApprovalTask;
  @Output() eventEmitRetrofitTimespan = new EventEmitter<IApprovalTask>();

  public currentUser: IUser;
  public innerWidth: number;
  public crossFieldErrorMatcherConstructionDates = new CrossFieldErrorMatcherConstructionDates();
  public formRetrofitTimespan: FormGroup;
  public formAutoSaveTimespan = false;
  public taskItemRejectedConstruction = false;
  public taskItemRejectedInspection = false;
  public taskItemRejectedConstructionReasonWhy = '';
  public taskItemRejectedInspectionReasonWhy = '';
  // Both of the following string values need to be evaluated, because of conflicting requirements
  // which were implemented in approximately the same timeframe.
  public construction = 'Construction';
  public construction2 = 'Construction Time Span';
  public inspection = 'Inspection';
  constructor(
    private fb: FormBuilder,
    private readonly authenticationService: AuthenticationService,
    private readonly infoSheet: MatBottomSheet,
    private apiApplication: ApplicationApiService
  ) { }

  ngOnInit() {
    this.currentUser = this.authenticationService.currentUserValue;
    this.innerWidth = window.innerWidth;
    this.initializeForms();
    this.populateForms();
  }

  private initializeForms(): void {
    this.formRetrofitTimespan = this.fb.group({
      constructionStartDate: ['', Validators.required],
      constructionEndDate: ['', Validators.required],
      constructionNumberOfDays: ['', Validators.required],
      inspectorFullname: ['', Validators.required],
      inspectionDate: ['', Validators.required]
    }, { validators: [this.startEndDateValidator] });
  }

  private populateForms(): void {
    if (this.application.retrofitConstructionStartDate) {
      this.formRetrofitTimespan.get('constructionStartDate').setValue(this.application.retrofitConstructionStartDate);
    }

    if (this.application.retrofitConstructionEndDate) {
      this.formRetrofitTimespan.get('constructionEndDate').setValue(this.application.retrofitConstructionEndDate);
    }

    if (this.application.retrofitConstructionNumberOfDays) {
      this.formRetrofitTimespan.get('constructionNumberOfDays').setValue(this.application.retrofitConstructionNumberOfDays);
    }

    if (this.application.inspectorFullname) {
      this.formRetrofitTimespan.get('inspectorFullname').setValue(this.application.inspectorFullname);
    }

    if (this.application.inspectionDate) {
      this.formRetrofitTimespan.get('inspectionDate').setValue(this.application.inspectionDate);
    }

    this.processRejectedItems();
  }

  public processRejectedItems(): void {
    this.task.approvalTaskItems.forEach(item => {
      // 700000002 = rejected
      if (item.statusCode === 700000002) {
        if (item.name === this.construction || item.name === this.construction2) {
          this.taskItemRejectedConstruction = true;
          this.taskItemRejectedConstructionReasonWhy = item.reasonWhy;
        } else if (item.name === this.inspection) {
          this.taskItemRejectedInspection = true;
          this.taskItemRejectedInspectionReasonWhy = item.reasonWhy;
        }
      }
    });

    if (this.disableArea(this.construction)) {
      this.formRetrofitTimespan.get('constructionStartDate').disable();
      this.formRetrofitTimespan.get('constructionEndDate').disable();
      this.formRetrofitTimespan.get('constructionNumberOfDays').disable();
    }

    if (this.disableArea(this.inspection)) {
        this.formRetrofitTimespan.get('inspectorFullname').disable();
        this.formRetrofitTimespan.get('inspectionDate').disable();
    }
  }

  // Timespan form contains two areas, a Construction area and an Inspection area.
  // To determine whether form fields should be disabled for either area,
  // evaluate criteria such as application stage and task item rejection status.
  private disableArea(areaName: string): boolean {
    if (this.application.stageCode === 700000001 && this.application.eventApplicationSubmitted === true && areaName === this.construction && this.taskItemRejectedConstruction) {
      return false;
    }

    if (this.application.stageCode === 700000001 && this.application.eventApplicationSubmitted === true && areaName === this.construction && !this.taskItemRejectedConstruction) {
      return true;
    }

    if (this.application.stageCode === 700000001 && this.application.eventApplicationSubmitted === true && areaName === this.inspection && this.taskItemRejectedInspection) {
      return false;
    }

    if (this.application.stageCode === 700000001 && this.application.eventApplicationSubmitted === true && areaName === this.inspection && !this.taskItemRejectedInspection) {
      return true;
    }

    if (this.isContractor() && this.application.contractorPermissionsCode === 700000003) {
      return true;
    }

    if (this.application.eventMitigationReviewSubmitted && areaName === this.construction && !this.taskItemRejectedConstruction) {
      return true;
    }

    if (this.application.eventMitigationReviewSubmitted && areaName === this.construction && this.taskItemRejectedConstruction) {
      return false;
    }

    if (this.application.eventMitigationReviewSubmitted && areaName === this.inspection && !this.taskItemRejectedInspection) {
      return true;
    }

    if (this.application.eventMitigationReviewSubmitted && areaName === this.inspection && this.taskItemRejectedInspection) {
      return false;
    }

    if (this.application.eventFinancialReviewSubmitted) {
      return true;
    }

    return false;
  }

  public onCheckTimespanControlValidators(controlName: string): boolean {
    const retVal = (this.formRetrofitTimespan.get(controlName).errors
                      && (this.formRetrofitTimespan.get(controlName).dirty || this.formRetrofitTimespan.get(controlName).touched));
    return retVal;
  }
  private startEndDateValidator(c: AbstractControl): { startEndDateOrder: boolean } {
    const startDate = c.get('constructionStartDate').value;
    const endDate = c.get('constructionEndDate').value;
    if ((startDate && endDate) && endDate < startDate) {
      return { startEndDateOrder: true }
    }
  }

  public maxDaysValidator(c: AbstractControl): boolean {
    const startDate = c.get('constructionStartDate').value;
    const endDate = c.get('constructionEndDate').value;
    const numDays = c.get('constructionNumberOfDays').value;
    let diffDays = 0;
    if (startDate && endDate) {
      diffDays = Math.floor(( Date.parse(endDate) - Date.parse(startDate) ) / 86400000)
    }
    if (!isNaN(numDays) && numDays > diffDays+1) {
      return true;
    }
    return false;
  }

  public onClickSaveRetrofitTimespan(): void {
    const applicationInput: IApplicationInput = {} as IApplicationInput;
    this.formAutoSaveTimespan = true;
    applicationInput.retrofitConstructionStartDate = this.formRetrofitTimespan.get('constructionStartDate').value;
    applicationInput.retrofitConstructionEndDate = this.formRetrofitTimespan.get('constructionEndDate').value;
    applicationInput.retrofitConstructionNumberOfDays = parseInt(this.formRetrofitTimespan.get('constructionNumberOfDays').value, 10);
    applicationInput.inspectorFullname = this.formRetrofitTimespan.get('inspectorFullname').value;
    applicationInput.inspectionDate = this.formRetrofitTimespan.get('inspectionDate').value;

    this.apiApplication
      .updateApplication(applicationInput, this.application.id)
      .subscribe(
        () => {
          this.markItemsAsDirty();
          this.eventEmitRetrofitTimespan.emit(this.task);
          this.formAutoSaveTimespan = false;
          this.formRetrofitTimespan.markAsPristine();
        },
        error => console.warn(`ERROR: onClickSaveRetrofitTimespan() - ${error}`)
      );
  }

  public markItemsAsDirty (): void {
    if (this.formRetrofitTimespan.get('constructionStartDate').dirty ||
      this.formRetrofitTimespan.get('constructionEndDate').dirty ||
      this.formRetrofitTimespan.get('constructionNumberOfDays').dirty) {
        const itemC: IApprovalTaskItem = this.task.approvalTaskItems.find(x => x.name === this.construction || x.name === this.construction2);
        if (itemC) {
          itemC.payloadName = 'dirty';
        }
        this.taskItemRejectedConstruction = false;
    }

    if (this.formRetrofitTimespan.get('inspectorFullname').dirty ||
      this.formRetrofitTimespan.get('inspectionDate').dirty) {
        const itemI: IApprovalTaskItem = this.task.approvalTaskItems.find(x => x.name === this.inspection);
        if (itemI) {
          itemI.payloadName = 'dirty';
        }
        this.taskItemRejectedInspection = false;
    }
  }

  public onClickShowReasonWhy(topic: string): void {
    const item: IHelp = {} as IHelp;
    item.question = 'Reason Why';
    item.answer = topic.replace(/(\r\n|\n|\r)/gm, '<br/>');
    this.infoSheet.open(AppSheetHelpComponent, { data: item });
  }

  /***     User Convenience Methods */
  public isHomeowner() : boolean {
    return this.authenticationService.isHomeowner(this.currentUser);
  }

  public isContractor(): boolean {
    return this.authenticationService.isContractor(this.currentUser);
  }
}
